import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './screens/Login';
import Home from './screens/Home';
import Orders from './screens/Orders';
import OrderDetail from './screens/OrderDetail';
import CreateOrder from './screens/CreateOrder';
import Conversations from './screens/Conversations';
import Notifications from './screens/Notifications';
import NotificationSettings from './screens/NotificationSettings';
import Search from './screens/Search';
import Profile from './screens/Profile';
import Settings from './screens/Settings';
import Members from './screens/Members';
import AICalls from './screens/AICalls';
import AICallDetail from './screens/AICallDetail';
import DoCalls from './screens/DoCalls';
import Sidebar from './components/Sidebar';
import BootScreen from './components/BootScreen';
import ScrollManager from './components/ScrollManager';
import Loader from './components/Loader';
import Watch from './screens/Watch';

function Shell() {
  const { fbUser, profile, loading, authError, build } = useAuth();

  if (loading) return <Loader text="Loading…" />;
  if (!fbUser) return <Login />;

  if (!profile) {
    return (
      <div className="full-center" style={{ flexDirection: 'column', padding: 24, textAlign: 'center' }}>
        <div className="logo-mark" style={{ marginBottom: 16 }}>💎</div>
        <h2>Account not found</h2>
        <p className="muted">No member record matches this login. Ask your admin to add your number, then try again.</p>
        {authError && (
          <pre style={{ marginTop: 12, padding: 12, background: '#fff', borderRadius: 12, fontSize: 12, maxWidth: 360, whiteSpace: 'pre-wrap', textAlign: 'left', color: 'var(--red)' }}>
            {authError}
          </pre>
        )}
        <p className="faint" style={{ fontSize: 11, marginTop: 8 }}>build {build}</p>
        <SignOutLink />
      </div>
    );
  }

  // Vendors/team log in with OTP — no mandatory password step (it was flaky on
  // phone accounts). Password can be set later from Profile if desired.

  return (
    <>
      <Sidebar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/order/:id" element={<OrderDetail />} />
        <Route path="/search" element={<Search />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/notification-settings" element={<NotificationSettings />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/conversations" element={<Conversations />} />

        {/* Admin + team only */}
        <Route path="/create" element={<RequireRole roles={['admin', 'team']}><CreateOrder /></RequireRole>} />
        <Route path="/edit/:id" element={<RequireRole roles={['admin', 'team']}><CreateOrder /></RequireRole>} />

        {/* Admin only */}
        <Route path="/members" element={<RequireRole roles={['admin']}><Members /></RequireRole>} />
        <Route path="/ai-calls" element={<RequireRole roles={['admin']}><AICalls /></RequireRole>} />
        <Route path="/ai-calls/:callId" element={<RequireRole roles={['admin']}><AICallDetail /></RequireRole>} />
        <Route path="/do-calls" element={<RequireRole roles={['admin', 'team']}><DoCalls /></RequireRole>} />
        <Route path="/settings" element={<RequireRole roles={['admin']}><Settings /></RequireRole>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function RequireRole({ roles, children }) {
  const { role } = useAuth();
  if (!roles.includes(role)) return <Navigate to="/" replace />;
  return children;
}

function SignOutLink() {
  const { logout } = useAuth();
  return <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={logout}>Sign out</button>;
}

// The branded opening: BootScreen holds for a beat, then tilts away (`leaving`)
// while the app fades up beneath it (the reveal). App data mounts immediately
// behind the boot screen, so nothing is actually delayed.
function Booted() {
  const [phase, setPhase] = useState('boot'); // 'boot' | 'leaving' | 'done'
  useEffect(() => {
    const t1 = setTimeout(() => setPhase('leaving'), 2000);
    const t2 = setTimeout(() => setPhase('done'), 2750);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <>
      <div className={`app-reveal ${phase === 'boot' ? 'pre' : 'in'}`}><Shell /></div>
      {phase !== 'done' && <BootScreen leaving={phase === 'leaving'} />}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollManager />
        <Routes>
          {/* Public branded video watch page — no login (shared with karigars). */}
          <Route path="/watch/:orderId" element={<Watch />} />
          <Route path="*" element={<Booted />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
